/** Hook con la lógica del wizard de selección de workspace (pasos, carga, selección, navegación). */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Empty, Spin } from "antd";
import { MdBusiness, MdStore } from "react-icons/md";
import { useAuthStore } from "@/stores/authStore";
import { useUIStore } from "@/stores/uiStore";
import { useWorkSession } from "@/hooks";
import { getDefaultPath } from "@/utils";
import { esSuperAdmin } from "@/domain";
import type { GrupoEmpresa, Sucursal } from "@/types";
import { getEmpresas, getGruposEmpresa, getSucursalesDeEmpresa } from "./workspaceData";

type PasoKey = "grupo" | "empresa" | "sucursal";

interface Paso {
  key: PasoKey;
  titulo: string;
  subtitulo: string;
}

interface OpcionEmpresa {
  codigo: string;
  nombre: string;
}

export const useWorkspaceWizard = () => {
  const { loggedUserInnerwork, activeSucursal } = useWorkSession();
  const setActiveSucursal = useAuthStore((s) => s.setActiveSucursal);
  const setSinSucursal = useAuthStore((s) => s.setSinSucursal);
  const updateLoggedUser = useAuthStore((s) => s.updateLoggedUser);
  const logout = useAuthStore((s) => s.logout);
  const currentModule = useUIStore((s) => s.currentModule);
  const itemsMenu = useUIStore((s) => s.itemsMenu);
  const navigate = useNavigate();
  const darkMode = useUIStore((s) => s.darkMode);

  const [paso, setPaso] = useState(0);
  const [cerrando, setCerrando] = useState(false);
  const [cargando, setCargando] = useState(true);
  const [mostrarSpinner, setMostrarSpinner] = useState(false);
  const [grupos, setGrupos] = useState<GrupoEmpresa[]>([]);
  const [empresas, setEmpresas] = useState<OpcionEmpresa[]>([]);
  const [sucursales, setSucursales] = useState<Sucursal[]>(loggedUserInnerwork.sucursales ?? []);
  const [grupoElegido, setGrupoElegido] = useState<string | undefined>(
    loggedUserInnerwork.codGrupoEmpresa
  );
  const [empresaElegida, setEmpresaElegida] = useState<string | undefined>(
    loggedUserInnerwork.codEmpresa
  );

  const pasos = useMemo<Paso[]>(() => {
    const lista: Paso[] = [];
    if (!loggedUserInnerwork.codGrupoEmpresa) {
      lista.push({ key: "grupo", titulo: "Grupo empresa", subtitulo: "Elegí el grupo empresa" });
    }
    const multiplesEmpresas = (loggedUserInnerwork.empresas?.length ?? 0) > 1;
    if (!loggedUserInnerwork.codEmpresa || multiplesEmpresas) {
      lista.push({ key: "empresa", titulo: "Empresa", subtitulo: "Elegí la empresa" });
    }
    lista.push({ key: "sucursal", titulo: "Sucursal", subtitulo: "Elegí tu sucursal de trabajo" });
    return lista;
  }, [
    loggedUserInnerwork.codGrupoEmpresa,
    loggedUserInnerwork.codEmpresa,
    loggedUserInnerwork.empresas,
  ]);

  const pasoActual = pasos[Math.min(paso, pasos.length - 1)];

  const nombreEmpresaContexto = useMemo(() => {
    const cod = empresaElegida ?? loggedUserInnerwork.codEmpresa;
    return (
      empresas.find((e) => e.codigo === cod)?.nombre ||
      loggedUserInnerwork.empresas?.find((e) => e.codEmpresa === cod)?.nombreEmpresa ||
      loggedUserInnerwork.nombreEmpresa ||
      ""
    );
  }, [
    empresaElegida,
    empresas,
    loggedUserInnerwork.codEmpresa,
    loggedUserInnerwork.empresas,
    loggedUserInnerwork.nombreEmpresa,
  ]);

  useEffect(() => {
    if (!esSuperAdmin(loggedUserInnerwork) && (loggedUserInnerwork.sucursales?.length ?? 0) === 0) {
      navigate(getDefaultPath(itemsMenu, currentModule), { replace: true });
    }
  }, [loggedUserInnerwork, itemsMenu, currentModule, navigate]);

  useEffect(() => {
    let activo = true;
    const cargar = async () => {
      setCargando(true);
      try {
        if (pasoActual.key === "grupo") {
          const data = await getGruposEmpresa();
          if (activo) setGrupos(data);
        } else if (pasoActual.key === "empresa") {
          const deLogin = (loggedUserInnerwork.empresas ?? []).map((e) => ({
            codigo: e.codEmpresa,
            nombre: e.nombreEmpresa,
          }));
          if (deLogin.length) {
            if (activo) setEmpresas(deLogin);
          } else {
            const data = await getEmpresas(grupoElegido ?? "");
            if (activo) setEmpresas(data.map((e) => ({ codigo: e.codigo, nombre: e.razonSocial })));
          }
        } else {
          const todas = loggedUserInnerwork.sucursales ?? [];
          if (todas.length) {
            const filtradas = empresaElegida
              ? todas.filter((s) => s.codEmpresa === empresaElegida)
              : todas;
            if (activo) setSucursales(filtradas);
          } else {
            const data = await getSucursalesDeEmpresa(empresaElegida ?? "");
            if (activo) setSucursales(data);
          }
        }
      } finally {
        if (activo) setCargando(false);
      }
    };
    cargar();
    return () => {
      activo = false;
    };
  }, [
    pasoActual.key,
    grupoElegido,
    empresaElegida,
    loggedUserInnerwork.sucursales,
    loggedUserInnerwork.empresas,
  ]);

  useEffect(() => {
    if (!cargando) {
      setMostrarSpinner(false);
      return;
    }
    const timer = window.setTimeout(() => setMostrarSpinner(true), 150);
    return () => window.clearTimeout(timer);
  }, [cargando]);

  const avanzar = () => setPaso((p) => Math.min(p + 1, pasos.length - 1));
  const volver = () => setPaso((p) => Math.max(p - 1, 0));

  const elegirGrupo = (codigo: string) => {
    setGrupoElegido(codigo);
    setEmpresaElegida(undefined);
    avanzar();
  };

  const elegirEmpresa = (codigo: string) => {
    setEmpresaElegida(codigo);
    avanzar();
  };

  const elegirSucursal = (idSucursal: number) => {
    const sucursal = sucursales.find((s) => s.idSucursal === idSucursal);
    if (!sucursal) return;
    const empresa = empresas.find((e) => e.codigo === empresaElegida);
    updateLoggedUser({
      codGrupoEmpresa: grupoElegido ?? loggedUserInnerwork.codGrupoEmpresa,
      codEmpresa: sucursal.codEmpresa || empresaElegida,
      nombreEmpresa: sucursal.nombreEmpresa || empresa?.nombre,
    });
    setActiveSucursal(sucursal);
    navigate(getDefaultPath(itemsMenu, currentModule));
  };

  const continuarSinSucursal = () => {
    updateLoggedUser({
      codGrupoEmpresa: grupoElegido ?? loggedUserInnerwork.codGrupoEmpresa,
      codEmpresa: empresaElegida ?? loggedUserInnerwork.codEmpresa,
      nombreEmpresa: nombreEmpresaContexto || loggedUserInnerwork.nombreEmpresa,
    });
    setSinSucursal();
    navigate(getDefaultPath(itemsMenu, currentModule));
  };

  const cerrarSesion = () => {
    setCerrando(true);
    window.setTimeout(() => {
      logout();
      navigate("/");
    }, 400);
  };

  const renderOpciones = () => {
    if (cargando) {
      return mostrarSpinner ? (
        <div className="select-workspace-loading">
          <Spin />
        </div>
      ) : (
        <div className="select-workspace-loading-placeholder" />
      );
    }

    if (pasoActual.key === "grupo") {
      if (!grupos.length) return <Empty description="No hay grupos empresa disponibles" />;
      return grupos.map((g) => (
        <Button
          key={g.codigo}
          className={`select-workspace-item ${grupoElegido === g.codigo ? "active" : ""}`}
          onClick={() => elegirGrupo(g.codigo)}
        >
          <MdBusiness size={20} />
          <span>{g.nombre}</span>
        </Button>
      ));
    }

    if (pasoActual.key === "empresa") {
      if (!empresas.length) return <Empty description="No hay empresas disponibles" />;
      return empresas.map((e) => (
        <Button
          key={e.codigo}
          className={`select-workspace-item ${empresaElegida === e.codigo ? "active" : ""}`}
          onClick={() => elegirEmpresa(e.codigo)}
        >
          <MdBusiness size={20} />
          <span>{e.nombre}</span>
        </Button>
      ));
    }

    if (!sucursales.length) return <Empty description="No hay sucursales disponibles" />;
    return sucursales.map((sucursal) => (
      <Button
        key={sucursal.idSucursal}
        className={`select-workspace-item ${
          activeSucursal?.idSucursal === sucursal.idSucursal ? "active" : ""
        }`}
        onClick={() => elegirSucursal(sucursal.idSucursal)}
      >
        <MdStore size={20} />
        <span>{sucursal.nombre}</span>
      </Button>
    ));
  };

  const nombreUsuario = [loggedUserInnerwork?.nombre, loggedUserInnerwork?.apellido]
    .filter(Boolean)
    .join(" ")
    .trim();

  return {
    loggedUserInnerwork,
    darkMode,
    paso,
    pasos,
    pasoActual,
    cargando,
    mostrarSpinner,
    grupos,
    empresas,
    sucursales,
    grupoElegido,
    empresaElegida,
    nombreEmpresaContexto,
    nombreUsuario,
    cerrando,
    renderOpciones,
    avanzar,
    volver,
    elegirGrupo,
    elegirEmpresa,
    elegirSucursal,
    continuarSinSucursal,
    cerrarSesion,
  };
};
